import type { ReactElement, ReactNode } from 'react';
import type {
  CellProps,
  CellState,
  Column,
  RenderCellProps,
  RenderEditCellProps,
  RenderHeaderCellProps,
  RenderSummaryCellProps,
} from 'components/DataGrid';
import type { FieldConfig } from 'modules/campaign/row';
import type { RowConfig } from 'modules/campaign/row/common';
import type { GridGroup, GridRow, GridSummaryRow, RowOrGroup } from '../../types';

export type BranchRenderProps<R extends RowOrGroup> = RenderCellProps<R, GridSummaryRow> & { field: string };

export type BranchEditProps<R extends RowOrGroup> = RenderEditCellProps<R, GridSummaryRow> & { field: string };

/** Bound to a FieldConfig on row.config; cellState comes from row.config[field]. */
export type FieldBranch<R extends GridRow> = {
  // FIXME: tighten to FieldKey<R> once read-only-with-string-field cases
  // (e.g. reach_*, application) are migrated to ReadonlyBranch.
  readonly field: keyof RowConfig | string;
  readonly view: (props: BranchRenderProps<R>) => ReactNode;
  readonly edit?: (props: BranchEditProps<R>) => ReactNode;
  readonly displayCellContent?: boolean;
};

/**
 * Group branch — groups carry a single `config.locked`, not per-field FieldConfig.
 * `field` is the editor's write target only; defaults to column.key when omitted.
 */
export type GroupBranch = {
  readonly field?: keyof GridGroup;
  readonly view: (props: BranchRenderProps<GridGroup>) => ReactNode;
  readonly edit?: (props: BranchEditProps<GridGroup>) => ReactNode;
  readonly displayCellContent?: boolean;
};

/** Pure read-only — locked, valid, non-empty, non-required. No editor, no field. */
export type ReadonlyBranch<R extends RowOrGroup> = {
  readonly view: (props: BranchRenderProps<R>) => ReactNode;
  readonly displayCellContent?: boolean;
  readonly field?: never;
  readonly edit?: never;
};

type Branch<R extends RowOrGroup> =
  | (R extends GridRow ? FieldBranch<R> : never)
  | (R extends GridGroup ? GroupBranch : never)
  | ReadonlyBranch<R>;

export const LOCKED_CELL_STATE: CellState = Object.freeze({
  locked: true,
  required: false,
  empty: false,
  valid: true,
});

type ShellArgs = {
  readonly key: string;
  readonly name?: string | ReactElement;
  readonly width?: number;
  readonly frozen?: boolean;
  readonly className?: string;
  readonly headerClassName?: string;
  readonly summaryClassName?: string;
  readonly renderHeader?: (p: RenderHeaderCellProps<RowOrGroup, GridSummaryRow>) => ReactNode;
  readonly renderSummary?: (p: RenderSummaryCellProps<GridSummaryRow, RowOrGroup>) => ReactNode;
};

/**
 * Thin shell wrapper — applies header/summary/width/frozen/className shell
 * args to a caller-supplied per-row `cell` resolver. Use when you need fully
 * custom dispatch; otherwise prefer `byKind`.
 */
export function planColumn(
  args: ShellArgs & {
    readonly cell: (row: RowOrGroup) => CellProps<RowOrGroup, GridSummaryRow>;
  },
): Column<RowOrGroup, GridSummaryRow> {
  const {
    key,
    name = '',
    width,
    frozen,
    className,
    headerClassName,
    summaryClassName,
    renderHeader,
    renderSummary,
    cell,
  } = args;

  return {
    key,
    name,
    width,
    minWidth: width,
    maxWidth: width,
    frozen,
    resizable: false,
    sortable: false,
    headerCellClass: headerClassName ?? className,
    summaryCellClass: summaryClassName ?? className,
    renderHeaderCell: renderHeader,
    renderSummaryCell: renderSummary,
    cell,
  };
}

export type ByKindBranches = {
  group?: GroupBranch | ReadonlyBranch<GridGroup>;
  smart?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
  media?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
  service?: FieldBranch<GridRow> | ReadonlyBranch<GridRow>;
};

export function byKind(args: ShellArgs & { branches: ByKindBranches }): Column<RowOrGroup, GridSummaryRow> {
  const { branches, ...shell } = args;
  const { key, className } = shell;

  return planColumn({
    ...shell,
    cell: (row) => {
      const b = pickBranch(row, branches);
      if (!b) return { className, ...LOCKED_CELL_STATE };

      const field = b.field ?? key;

      // Cast at the dispatch boundary: pickBranch selects by row kind, so
      // widening the branch's narrow callbacks back to RowOrGroup is safe —
      // they will only be invoked with the row that selected them.
      const view = b.view as (p: BranchRenderProps<RowOrGroup>) => ReactNode;
      const edit = b.edit as ((p: BranchEditProps<RowOrGroup>) => ReactNode) | undefined;

      return {
        className,
        renderCell: (p) => view({ ...p, field }),
        renderEditCell: edit ? (p) => edit({ ...p, field }) : undefined,
        displayCellContent: b.displayCellContent,
        ...deriveState(row, b),
      };
    },
  });
}

function pickBranch(row: RowOrGroup, branches: ByKindBranches): Branch<RowOrGroup> | undefined {
  if (row._isGroup) return branches.group as Branch<RowOrGroup> | undefined;
  const slot = row.kind.toLowerCase() as 'smart' | 'media' | 'service';
  return branches[slot] as Branch<RowOrGroup> | undefined;
}

function deriveState(row: RowOrGroup, b: Branch<RowOrGroup>): CellState {
  if (row._isGroup) {
    return {
      locked: row.config.locked || !b.edit,
      required: false,
      empty: false,
      valid: true,
    };
  }

  if (b.field !== undefined) {
    return (row.config as Record<string, FieldConfig | undefined>)[b.field] ?? LOCKED_CELL_STATE;
  }

  return LOCKED_CELL_STATE;
}
